import { NAV_ITEMS, NavItemKey } from "../navItems";
import {
  clearRememberedNavigation,
  notifyRememberedNavigationChanged,
  rememberedNavigationArea,
  rememberedNavigationStorageKey,
} from "./rememberedNavigationPersistence";
import { isDashboardPath } from "../dashboardStartup";

export type RememberedSectionRoute = {
  entityKey: string;
  path: string;
  label: string;
};

const STORAGE_KEY = rememberedNavigationArea("frontend").storageKey;
const MAX_ROUTES_PER_SECTION = 2;
export const REMEMBERED_ROUTES_CHANGED_EVENT =
  rememberedNavigationArea("frontend").changedEvent;
export const REMEMBERED_ROUTES_STORAGE_KEY = STORAGE_KEY;
let suppressRememberedRoutesUntil = 0;
let rememberedRoutesScope = "";

const scopedStorageKey = () =>
  rememberedNavigationStorageKey("frontend", rememberedRoutesScope);

export function setRememberedRoutesScope(scope: string) {
  rememberedRoutesScope = scope.trim();
}

const NAV_ITEM_KEYS = new Set<NavItemKey>(NAV_ITEMS.map((item) => item.key));
const SECTION_PATHS = [...NAV_ITEMS].sort(
  (a, b) => b.path.length - a.path.length
);

const ROOT_PATH_BY_KEY = NAV_ITEMS.reduce<Record<string, string>>((acc, item) => {
  acc[item.key] = item.path;
  return acc;
}, { helpCenter: "/help-center" });

function isNavItemKey(value: string): value is NavItemKey {
  return NAV_ITEM_KEYS.has(value as NavItemKey);
}

function readMap(): Partial<Record<NavItemKey, RememberedSectionRoute[]>> {
  try {
    const key = scopedStorageKey();
    if (!key) return {};
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return Object.entries(parsed).reduce<
      Partial<Record<NavItemKey, RememberedSectionRoute[]>>
    >((acc, [key, routes]) => {
      if (!isNavItemKey(key) || !Array.isArray(routes)) return acc;
      const validRoutes = routes
        .map(normalizeRememberedRoute)
        .filter((route): route is RememberedSectionRoute => Boolean(route))
      .filter((route) => isRememberableRoute(key, route.path))
        .filter((route) => route.path !== ROOT_PATH_BY_KEY[key])
        .slice(0, MAX_ROUTES_PER_SECTION);
      if (validRoutes.length) acc[key] = validRoutes;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function writeMap(map: Partial<Record<NavItemKey, RememberedSectionRoute[]>>) {
  try {
    const key = scopedStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(map));
    notifyRememberedNavigationChanged("frontend");
  } catch {
    // ignore storage errors
  }
}

export function clearRememberedRoutes() {
  clearRememberedNavigation("frontend");
}

export function clearRememberedRoutesForContextReset() {
  suppressRememberedRoutesUntil = Date.now() + 1000;
  notifyRememberedNavigationChanged("frontend");
}

const normalizePath = (path: string) => path.replace(/\/+$/, "") || "/";

function normalizedPathname(path: string): string {
  return normalizePath(path.split("?")[0] || "");
}

const isRootPath = (key: NavItemKey, path: string) => {
  if (path.includes("?")) return false;
  return normalizedPathname(path) === normalizePath(ROOT_PATH_BY_KEY[key] || "");
};

function isRememberableRoute(key: NavItemKey, path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  if (isRootPath(key, path)) return false;
  if (getSectionKeyForPath(normalizedPathname(path)) !== key) return false;
  return true;
}

function normalizeRememberedRoute(value: unknown): RememberedSectionRoute | null {
  if (!value || typeof value !== "object") return null;
  const route = value as Partial<RememberedSectionRoute>;
  const entityKey = typeof route.entityKey === "string" ? route.entityKey.trim() : "";
  const path = typeof route.path === "string" ? route.path.trim() : "";
  const label = typeof route.label === "string" ? route.label.trim() : "";
  if (!entityKey || !path || !label) return null;
  return {
    entityKey: entityKey.slice(0, 120),
    path,
    label: label.slice(0, 80),
  };
}

function routesEqual(
  a: RememberedSectionRoute[] = [],
  b: RememberedSectionRoute[] = [],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (route, index) =>
        route.entityKey === b[index]?.entityKey &&
        route.path === b[index]?.path &&
        route.label === b[index]?.label,
    )
  );
}

export function getSectionKeyForPath(pathname: string): NavItemKey | null {
  if (isDashboardPath(pathname)) return "dashboard";
  if (pathname.startsWith("/help-center")) return "helpCenter";
  const match = SECTION_PATHS.find((item) => pathname.startsWith(item.path));
  return match?.key ?? null;
}

export function readRememberedRoutes(key: NavItemKey): RememberedSectionRoute[] {
  const routes = readMap()[key];
  return Array.isArray(routes)
    ? routes
        .filter((route) => isRememberableRoute(key, route.path))
        .slice(0, MAX_ROUTES_PER_SECTION)
    : [];
}

export function readLastRoute(key: NavItemKey): string | null {
  return readRememberedRoutes(key)[0]?.path || null;
}

export function writeLastRoute(
  key: NavItemKey,
  path: string,
  route?: Partial<RememberedSectionRoute>,
) {
  if (Date.now() < suppressRememberedRoutesUntil) return;
  if (!isRememberableRoute(key, path)) return;
  const label = (route?.label || "").trim();
  if (!label) return;
  const entityKey = (route?.entityKey || path).trim();
  if (!entityKey) return;
  const map = readMap();
  const previous = map[key] || [];
  const nextRoute: RememberedSectionRoute = {
    entityKey: entityKey.slice(0, 120),
    path,
    label: label.slice(0, 80),
  };
  const next = [
    nextRoute,
    ...previous.filter(
      (entry) => entry.entityKey !== entityKey && entry.path !== path,
    ),
  ].slice(0, MAX_ROUTES_PER_SECTION);
  if (routesEqual(previous, next)) return;
  map[key] = next;
  writeMap(map);
}

export function removeRememberedRoute(key: NavItemKey, entityKey: string) {
  const map = readMap();
  const next = (map[key] || []).filter((entry) => entry.entityKey !== entityKey);
  if (next.length === (map[key] || []).length) return;
  if (next.length) map[key] = next;
  else delete map[key];
  writeMap(map);
}

export function removeLastRoutesMatching(
  predicate: (path: string, key: NavItemKey) => boolean
) {
  const map = readMap();
  let changed = false;
  (Object.entries(map) as Array<[NavItemKey, RememberedSectionRoute[]]>).forEach(
    ([key, routes]) => {
      const next = routes.filter((route) => !predicate(route.path, key));
      if (next.length === routes.length) return;
      changed = true;
      if (next.length) map[key] = next;
      else delete map[key];
    },
  );
  if (changed) writeMap(map);
}
